import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { ReadyState } from 'react-use-websocket'
import { MiwaAppWebsocketContext } from '@/contexts/miwa-app-websocket'
import { fetchGuestRoomForReservation, updateMiwaCardIssuesLogs } from '@/apis/aipass'
import { AdminReservationResponseType } from '@/models/reservation/admin-reservation'
import { TMiwaAppWebsocketContext, TMiwaLockStatus, TMiwaState } from '@/models/miwa-lock'
import { EditRoomType } from '@/hooks/use-edit-guest-rooms'
import {
  MiwaLockStatus,
  MiwaLockWebsocketRequestEvent,
  MiwaLockWebsocketResponseEvent,
  MiwaLockWebsocketResponseStatusCode,
} from '@/constants/miwa-lock'
import { calcCardStartAt, calcIssueStartAt, splitCardEndAt } from '@/utils/miwa-lock'
import { LoadingFull } from '@/components/molecules/loading-full'
import { KeyCardWriteModal } from './key-card-write-modal'
import { KeyCardWriteProgressModal } from './key-card-write-progress-modal'

export type KeyCardWriteHandle = {
  // 部屋割の発行・再発行ボタンから呼び、書き込みの設定モーダルを開く
  open: (room: EditRoomType) => Promise<void>
}

type Props = {
  reservation: AdminReservationResponseType['reservations']
  // 1 枚以上書き込めて発行ログを保存した後に呼ぶ（部屋割の発行状態を取り直すため）
  onWritten: () => Promise<void>
}

const initialMiwaState: TMiwaState = {
  numberOfCards: 0,
  numberOfCardsReleaseSuccess: 0,
  guestRoomId: '',
  guestRoomAssignId: '',
  checkinTime: '',
  checkinDate: '',
  lastCheckinDatetime: '',
  checkoutTime: '',
  checkoutDate: '',
  typeNameJa: '',
  roomNumber: '',
  issueType: '',
  miwaActionType: '',
  hotelOnSiteCode: undefined,
  issueCount: 1,
}

/**
 * 美和ロックのカードキー書き込み（対面発行）
 * 書き込みの設定 → 美和アプリへの送信 → 書き込み中〜完了の表示 → 発行ログの保存までを受け持つ
 */
export const KeyCardWrite = forwardRef<KeyCardWriteHandle, Props>(({ reservation, onWritten }, ref) => {
  const { t } = useTranslation()
  const {
    lastJsonMessage,
    sendJsonMessage,
    miwaAppWebsocketPCID,
    miwaAppWebsocketHotelOnSiteCode,
    miwaAppWebsocketErrorMessage,
    setMiwaAppWebsocketErrorMessage,
    fetchConnectionInfo,
    isLoadingFetchConnectionInfo,
    readyState,
  } = useContext<TMiwaAppWebsocketContext>(MiwaAppWebsocketContext)

  const [miwaState, setMiwaState] = useState<TMiwaState>(initialMiwaState)
  const [isOpenWriteModal, setIsOpenWriteModal] = useState(false)
  const [isOpenWriteProgressModal, setIsOpenWriteProgressModal] = useState(false)
  const [isSavingLog, setIsSavingLog] = useState(false)
  // 未接続でボタンを押した場合、接続（認証）の完了を待ってから設定モーダルを開く
  const [isWaitingAuthToOpen, setIsWaitingAuthToOpen] = useState(false)
  // 美和アプリへ送ってカードに書き込んだ開始日時。発行ログにはこの値をそのまま保存する（送信時と保存時で再計算しない）
  const [writtenCardStartAt, setWrittenCardStartAt] = useState('')
  const [isLoadingLatestRoom, setIsLoadingLatestRoom] = useState(false)

  /**
   * 書き込む部屋割を取り直す。見つからない・取得に失敗した場合は undefined
   * 一覧は画面を開いた時点の値のため、その後にセルフ CI や別端末で発行されていると古い発行状況・有効期間で書き込んでしまう
   * 一覧の state は更新しない（部屋割の編集中の内容を消さないため。一覧は書き込み後の onWritten で取り直す）
   */
  const fetchLatestRoom = async (guestRoomAssignId: string | undefined): Promise<EditRoomType | undefined> => {
    if (!guestRoomAssignId) return undefined
    setIsLoadingLatestRoom(true)
    try {
      const rooms = await fetchGuestRoomForReservation(reservation.reservationId)
      return rooms ? rooms.find(r => r.guestRoomAssignId === guestRoomAssignId) : undefined
    } finally {
      setIsLoadingLatestRoom(false)
    }
  }

  useImperativeHandle(ref, () => ({
    open: async (listedRoom: EditRoomType) => {
      if (!miwaAppWebsocketPCID) {
        window.alert(t('Please input PCID and try again.'))
        return
      }

      const room = await fetchLatestRoom(listedRoom.guestRoomAssignId)
      if (!room) {
        window.alert(t('Unable to get the latest card issue status. Please try again.'))
        return
      }

      const plannedCheckinDate = room.assignCheckinDate ? room.assignCheckinDate.split(' ')[0] : ''
      const issueStartAt = calcIssueStartAt(room.issueType ?? '', plannedCheckinDate)
      const cardEndAt = room.assignCheckoutDate && room.checkoutTime ? splitCardEndAt(room.assignCheckoutDate, room.checkoutTime) : null

      setIsWaitingAuthToOpen(true)
      setMiwaState({
        ...initialMiwaState,
        numberOfCards: room.requiredCardCount ?? 0,
        guestRoomId: room.guestRoomId || '',
        guestRoomAssignId: room.guestRoomAssignId || '',
        checkinTime: issueStartAt ? issueStartAt.format('HH:mm') : room.checkinTime || '',
        checkinDate: issueStartAt ? issueStartAt.format('YYYY-MM-DD') : plannedCheckinDate,
        lastCheckinDatetime: room.lastCheckinDatetime || '',
        checkoutTime: cardEndAt?.time ?? '',
        checkoutDate: cardEndAt?.date ?? '',
        typeNameJa: room.typeNameJa || '',
        roomNumber: room.roomNumber || '',
        issueType: room.issueType || '',
        issueCount: room.issueCount || 1,
      })

      if (readyState === ReadyState.OPEN) {
        setIsOpenWriteModal(true)
      } else {
        await fetchConnectionInfo()
      }
    },
  }))

  const closeWriteModal = () => {
    setMiwaState(initialMiwaState)
    setIsWaitingAuthToOpen(false)
    setIsOpenWriteModal(false)
  }

  const startWrite = (type: TMiwaLockStatus) => {
    const issueCount = type === MiwaLockStatus.REISSUE ? miwaState.issueCount + 1 : miwaState.issueCount
    const cardStartAt = calcCardStartAt(type, miwaState)

    setMiwaState({ ...miwaState, issueCount, miwaActionType: type })
    setWrittenCardStartAt(cardStartAt)

    const sendMessage = {
      reservation_id: reservation?.id?.toUpperCase(),
      issue_at: dayjs().format('YYYY-MM-DD HH:mm'),
      checkin_date: cardStartAt,
      checkout_date: dayjs(`${miwaState.checkoutDate} ${miwaState.checkoutTime}`).format('YYYY-MM-DD HH:mm'),
      room_number: Number(miwaState.roomNumber),
      number_of_cards: miwaState.numberOfCards,
    }
    if (miwaAppWebsocketHotelOnSiteCode) {
      Object.assign(sendMessage, {
        hotel_on_site_code: miwaAppWebsocketHotelOnSiteCode,
        issue_count: issueCount,
      })
    }
    sendJsonMessage({ type, body: sendMessage })

    setIsOpenWriteModal(false)
    setIsWaitingAuthToOpen(false)
    setIsOpenWriteProgressModal(true)
  }

  const closeWriteProgressModal = async () => {
    setIsOpenWriteProgressModal(false)
    miwaAppWebsocketErrorMessage && setMiwaAppWebsocketErrorMessage('')

    // 書き込みが終わる前に閉じた場合は、美和アプリ側の書き込み待ちを止める
    if (miwaState.numberOfCardsReleaseSuccess < miwaState.numberOfCards) {
      sendJsonMessage({ type: MiwaLockWebsocketRequestEvent.CANCEL })
    }

    if (miwaState.numberOfCardsReleaseSuccess > 0) {
      try {
        setIsSavingLog(true)
        await updateMiwaCardIssuesLogs({
          guestRoomId: miwaState.guestRoomId,
          guestRoomAssignId: miwaState.guestRoomAssignId,
          checkinTime: miwaState.checkinTime,
          lastCheckinDatetime: dayjs(writtenCardStartAt).format('YYYY-MM-DD HH:mm:ss'),
          checkoutTime: miwaState.checkoutTime,
          issueType: miwaState.miwaActionType,
          issueCount: miwaState.issueCount,
          requestedCards: miwaState.numberOfCards,
          issuedCards: miwaState.numberOfCardsReleaseSuccess,
          payload: miwaState,
        })
        await onWritten()
      } catch (error) {
        console.log('Fail To Update MiwaCardIssuesLogs: ', error)
      } finally {
        setIsSavingLog(false)
      }
    }

    setMiwaState(initialMiwaState)
    setWrittenCardStartAt('')
  }

  const onWriteResult = (statusCode: number) => {
    if (statusCode === MiwaLockWebsocketResponseStatusCode.ISSUE_IN_PROGRESS) {
      setMiwaState(prev => ({ ...prev, numberOfCardsReleaseSuccess: prev.numberOfCardsReleaseSuccess + 1 }))
    }
    if (statusCode !== MiwaLockWebsocketResponseStatusCode.ISSUE_DONE) {
      miwaAppWebsocketErrorMessage && setMiwaAppWebsocketErrorMessage('')
    }
  }

  useEffect(() => {
    if (lastJsonMessage === null) return
    const { type, body } = lastJsonMessage

    switch (type) {
      case MiwaLockWebsocketResponseEvent.AUTH_RESULT:
        if (isWaitingAuthToOpen && body.status_code === MiwaLockWebsocketResponseStatusCode.AUTH_SUCCESS) {
          setIsOpenWriteModal(true)
        }
        break
      case MiwaLockWebsocketResponseEvent.ISSUE_RESULT:
      case MiwaLockWebsocketResponseEvent.REISSUE_RESULT:
      case MiwaLockWebsocketResponseEvent.ADDITIONAL_ISSUE_RESULT:
        onWriteResult(body.status_code)
        break
      default:
        break
    }
  }, [lastJsonMessage])

  return (
    <>
      {isOpenWriteModal && (
        <KeyCardWriteModal miwaState={miwaState} setMiwaState={setMiwaState} onClickModalCancel={closeWriteModal} onClickModalSave={startWrite} />
      )}
      {isOpenWriteProgressModal && (
        <KeyCardWriteProgressModal
          miwaState={miwaState}
          setMiwaState={setMiwaState}
          numberOfCards={miwaState.numberOfCards}
          numberOfCardsReleaseSuccess={miwaState.numberOfCardsReleaseSuccess}
          onClickModalCancel={closeWriteProgressModal}
        />
      )}
      <LoadingFull isLoading={isSavingLog || isLoadingFetchConnectionInfo || isLoadingLatestRoom} />
    </>
  )
})

KeyCardWrite.displayName = 'KeyCardWrite'
